import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import * as Location from 'expo-location';
import { apiService } from '../services/api';
import { CapturedPosition, getCurrentPosition, haversineMeters } from '../utils/visitCapture';

// The route is re-fetched once the agent has moved this far from where the
// last route started, at most this often — the line shrinks (or re-routes)
// as they drive without a request on every GPS tick.
const ROUTE_REFRESH_METERS = 25;
const ROUTE_REFRESH_MIN_MS = 8000;

// If the GPS watch hasn't reported for this long, ask for a fresh fix
// directly — some Android devices and mobile browsers silently stop
// delivering watch updates, which froze the marker at the starting point.
const FALLBACK_POLL_MS = 5000;

/**
 * Live agent position + road route to a visit's target, for the fullscreen
 * directions maps. While `active`, tracks the device with a high-accuracy
 * GPS watch (native every 3s/10m, browser watchPosition on web), backed by
 * a fresh fix every 5s whenever the watch goes quiet, and keeps the route
 * current. Everything stops and resets when inactive.
 */
export function useLiveDirections(visitId: string | null | undefined, active: boolean, initialPosition?: CapturedPosition | null) {
  const [position, setPosition] = useState<CapturedPosition | null>(null);
  const [geometry, setGeometry] = useState<Array<[number, number]>>([]);
  const lastRouteRef = useRef<{ latitude: number; longitude: number; at: number } | null>(null);
  const inFlightRef = useRef(false);

  const refreshRoute = useCallback(async (p: CapturedPosition, force = false) => {
    if (!visitId) return;
    const last = lastRouteRef.current;
    if (!force && last) {
      const moved = haversineMeters(last.latitude, last.longitude, p.latitude, p.longitude);
      if (moved < ROUTE_REFRESH_METERS || Date.now() - last.at < ROUTE_REFRESH_MIN_MS) return;
    }
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    lastRouteRef.current = { latitude: p.latitude, longitude: p.longitude, at: Date.now() };
    try {
      const res = await apiService.getVisitLiveDirections(visitId, p.latitude, p.longitude, 'driving');
      if (Array.isArray(res.data?.geometry)) setGeometry(res.data.geometry);
    } catch {
      // Keep the previous line; the next position update tries again.
    } finally {
      inFlightRef.current = false;
    }
  }, [visitId]);

  useEffect(() => {
    if (!active || !visitId) {
      setPosition(null);
      setGeometry([]);
      lastRouteRef.current = null;
      return;
    }
    let cancelled = false;
    let subscription: { remove: () => void } | null = null;
    let pollTimer: ReturnType<typeof setInterval> | null = null;

    const onPosition = (p: CapturedPosition, force = false) => {
      if (cancelled) return;
      setPosition(p);
      refreshRoute(p, force);
    };

    // Show something right away: a position the caller already has, then a
    // fresh fix (which also draws the first route).
    if (initialPosition) onPosition(initialPosition, true);
    getCurrentPosition().then((p) => onPosition(p, !initialPosition)).catch(() => {});

    let lastFixAt = Date.now();
    let webWatchId: number | null = null;
    const onWatch = (p: CapturedPosition) => {
      lastFixAt = Date.now();
      onPosition(p);
    };

    if (Platform.OS === 'web') {
      if (typeof navigator !== 'undefined' && navigator.geolocation) {
        webWatchId = navigator.geolocation.watchPosition(
          (pos) => onWatch({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracyMeters: pos.coords.accuracy }),
          () => {},
          { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 }
        );
      }
    } else {
      Location.requestForegroundPermissionsAsync()
        .then(({ status }) => {
          if (status !== 'granted' || cancelled) return null;
          return Location.watchPositionAsync(
            { accuracy: Location.Accuracy.BestForNavigation, timeInterval: 3000, distanceInterval: 5 },
            (loc) => onWatch({
              latitude: loc.coords.latitude,
              longitude: loc.coords.longitude,
              accuracyMeters: loc.coords.accuracy ?? undefined,
            })
          );
        })
        .then((sub) => {
          if (!sub) return;
          if (cancelled) sub.remove();
          else subscription = sub;
        })
        .catch(() => {});
    }

    // Backup: a fresh fix whenever the watch has gone quiet
    let polling = false;
    pollTimer = setInterval(() => {
      if (polling || Date.now() - lastFixAt < FALLBACK_POLL_MS) return;
      polling = true;
      getCurrentPosition({ maxAgeMs: 0 })
        .then((p) => { lastFixAt = Date.now(); onPosition(p); })
        .catch(() => {})
        .finally(() => { polling = false; });
    }, FALLBACK_POLL_MS);

    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
      if (webWatchId !== null && typeof navigator !== 'undefined') navigator.geolocation?.clearWatch(webWatchId);
      try {
        subscription?.remove();
      } catch {
        // expo-location's web emitter can throw on remove; harmless on teardown
      }
    };
    // initialPosition is only a starting hint; changing it later shouldn't restart tracking
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, visitId, refreshRoute]);

  return { position, geometry };
}
